/**
 * Theme helpers. Mirrors the FOUC-prevention inline script in index.html —
 * any change here must also be reflected in the <head> snippet or the first
 * paint will flash the wrong palette.
 */
const STORAGE_KEY = 'ww-theme';
/**
 * Reads the persisted theme choice from localStorage, defaulting to `auto`
 * if nothing was stored (or storage is unavailable, e.g. SSR).
 */
export function getStoredTheme() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === 'light' || raw === 'dark' || raw === 'auto')
            return raw;
    }
    catch {
        /* ignore */
    }
    return 'auto';
}
/** Resolves `auto` to the current system preference. */
export function resolveEffectiveTheme(theme) {
    if (theme === 'light' || theme === 'dark')
        return theme;
    if (typeof window === 'undefined')
        return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Persists the user's choice and updates the `theme-*` class on <html>.
 * Safe to call during render; no-ops during SSR.
 */
export function applyTheme(theme) {
    if (typeof document === 'undefined')
        return;
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    }
    catch {
        /* ignore */
    }
    const effective = resolveEffectiveTheme(theme);
    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(`theme-${effective}`);
}
/**
 * Subscribes to OS-level colour-scheme changes. Returns an unsubscribe fn.
 */
export function watchSystemTheme(callback) {
    if (typeof window === 'undefined')
        return () => { };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (e) => callback(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
}
